import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Button,
  Chip,
  Card,
  CardContent,
  Grid,
  TextField,
  InputAdornment,
  Alert,
  CircularProgress,
} from '@mui/material';
import {
  Add as AddIcon,
  Visibility as ViewIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Search as SearchIcon,
  AttachMoney as MoneyIcon,
  Receipt as ReceiptIcon,
  Warning as WarningIcon,
  CheckCircle as CheckCircleIcon,
  Schedule as ScheduleIcon,
} from '@mui/icons-material';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'react-hot-toast';
import { format, parseISO } from 'date-fns';
import { useAuth } from '../../contexts/AuthContext';
import CaseDialog from '../../components/Cases/CaseDialog';
import ReceiptExpenseUpload from '../../components/Billing/ReceiptExpenseUpload';
import CreateFromUnbilledDialog from '../../components/Billing/CreateFromUnbilledDialog';
import ListLoadErrorAlert from '../../components/Common/ListLoadErrorAlert';
import ListPagination from '../../components/Common/ListPagination';
import { getAuthToken } from '../../lib/authToken';

interface BillableCase {
  id: string;
  caseNumber: string;
  title: string;
  description: string | null;
  status: 'open' | 'pending' | 'active' | 'on_hold' | 'closed' | 'archived';
  caseType: string | null;
  court: string | null;
  judge: string | null;
  filingDate: string | null;
  closingDate: string | null;
  estimatedValue: number | null;
  priority: 'low' | 'medium' | 'high' | 'urgent';
  tags: string[];
  metadata: any;
  assignedTo: string | null;
  client: {
    id: string;
    firstName: string;
    lastName: string;
    email: string;
    phone: string;
  } | null;
  assignedUser: {
    id: string;
    firstName: string;
    lastName: string;
    email: string;
  } | null;
  creator: {
    id: string;
    firstName: string;
    lastName: string;
    email: string;
  } | null;
  createdAt: string;
  updatedAt: string;
}

interface BillableCasesResponse {
  success: boolean;
  data: {
    cases: BillableCase[];
    pagination: {
      page: number;
      limit: number;
      total: number;
      pages: number;
    };
  };
}

const BillableCasesPage: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCase, setSelectedCase] = useState<BillableCase | null>(null);
  const [dialogMode, setDialogMode] = useState<'view' | 'edit' | 'create'>('view');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [expenseOpen, setExpenseOpen] = useState(false);
  const [unbilledOpen, setUnbilledOpen] = useState(false);
  const [unbilledCase, setUnbilledCase] = useState<BillableCase | null>(null);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(50);

  useEffect(() => {
    setPage(1);
  }, [searchQuery]);
  const queryClient = useQueryClient();

  // Fetch billable cases
  const { data: casesData, isLoading, error, refetch } = useQuery({
    queryKey: ['billable-cases', user?.tenantId, searchQuery, page, limit],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (searchQuery) params.append('q', searchQuery);
      params.set('limit', String(limit));
      params.set('offset', String((page - 1) * limit));

      const response = await fetch(`/api/billing/cases?${params.toString()}`, {
        headers: {
          'Authorization': `Bearer ${getAuthToken()}`,
        },
      });
      if (!response.ok) {
        throw new Error('Failed to fetch billable cases');
      }
      return response.json() as Promise<BillableCasesResponse>;
    },
  });

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'open':
        return 'success';
      case 'active':
        return 'primary';
      case 'pending':
        return 'warning';
      case 'on_hold':
        return 'info';
      case 'closed':
        return 'default';
      case 'archived':
        return 'error';
      default:
        return 'default';
    }
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'urgent':
        return 'error';
      case 'high':
        return 'warning';
      case 'medium':
        return 'info';
      case 'low':
        return 'success';
      default:
        return 'default';
    }
  };

  const formatCurrency = (amount: number | null) => {
    if (!amount) return 'N/A';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(amount);
  };

  const formatDate = (dateString: string | null) => {
    if (!dateString) return 'N/A';
    return format(parseISO(dateString), 'MMM dd, yyyy');
  };

  const cases = casesData?.data?.cases || [];
  const casesTotal = casesData?.data?.pagination?.total || cases.length;

  const handleViewCase = (caseItem: BillableCase) => {
    console.log('🔍 View case clicked:', caseItem);
    setSelectedCase(caseItem);
    setDialogMode('view');
    setDialogOpen(true);
  };

  const handleEditCase = (caseItem: BillableCase) => {
    console.log('🔍 Edit case clicked:', caseItem);
    setSelectedCase(caseItem);
    setDialogMode('edit');
    setDialogOpen(true);
  };

  const handleDialogClose = () => {
    console.log('🔍 Dialog closing');
    setDialogOpen(false);
    // Delay clearing selectedCase to allow dialog to finish closing animation
    setTimeout(() => {
      setSelectedCase(null);
    }, 300);
  };

  return (
    <Box sx={{ p: 3 }}>
      {error && (
        <ListLoadErrorAlert
          resourceLabel="billable cases"
          onRetry={() => refetch()}
        />
      )}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, flexWrap: 'wrap', gap: 1 }}>
        <Typography variant="h4" component="h1">
          Billable Cases
        </Typography>
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          <Button
            variant="outlined"
            startIcon={<MoneyIcon />}
            onClick={() => {
              setUnbilledCase(null);
              setUnbilledOpen(true);
            }}
          >
            Invoice unbilled
          </Button>
          <Button
            variant="outlined"
            startIcon={<ReceiptIcon />}
            onClick={() => setExpenseOpen(true)}
          >
            Upload Receipt
          </Button>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => navigate('/cases')}
          >
            Create Case
          </Button>
        </Box>
      </Box>

      {/* Search Bar */}
      <Box sx={{ mb: 3 }}>
        <TextField
          fullWidth
          placeholder="Search cases by case number or title..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
        />
      </Box>

      {/* Cases Table */}
      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Open & Billable Cases
          </Typography>
          
          {isLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
              <CircularProgress />
            </Box>
          ) : cases.length === 0 ? (
            <Alert severity="info">
              No billable cases found. Create a new case to get started.
            </Alert>
          ) : (
            <>
            <TableContainer component={Paper} variant="outlined">
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Case #</TableCell>
                    <TableCell>Title</TableCell>
                    <TableCell>Client</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Priority</TableCell>
                    <TableCell>Estimated Value</TableCell>
                    <TableCell>Assigned To</TableCell>
                    <TableCell>Created</TableCell>
                    <TableCell align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {cases.map((caseItem) => (
                    <TableRow key={caseItem.id} hover>
                      <TableCell>
                        <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>
                          {caseItem.caseNumber}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {caseItem.title}
                        </Typography>
                        {caseItem.description && (
                          <Typography variant="caption" color="text.secondary">
                            {caseItem.description.substring(0, 50)}...
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        {caseItem.client ? (
                          <Box>
                            <Typography variant="body2">
                              {caseItem.client.firstName} {caseItem.client.lastName}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {caseItem.client.email}
                            </Typography>
                          </Box>
                        ) : (
                          <Typography variant="body2" color="text.secondary">
                            No client assigned
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={caseItem.status}
                          color={getStatusColor(caseItem.status) as any}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={caseItem.priority}
                          color={getPriorityColor(caseItem.priority) as any}
                          size="small"
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                          {formatCurrency(caseItem.estimatedValue)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        {caseItem.assignedUser ? (
                          <Box>
                            <Typography variant="body2">
                              {caseItem.assignedUser.firstName} {caseItem.assignedUser.lastName}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {caseItem.assignedUser.email}
                            </Typography>
                          </Box>
                        ) : (
                          <Typography variant="body2" color="text.secondary">
                            Unassigned
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {formatDate(caseItem.createdAt)}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <IconButton 
                          size="small" 
                          color="primary"
                          title="View"
                          onClick={() => handleViewCase(caseItem)}
                        >
                          <ViewIcon />
                        </IconButton>
                        <IconButton
                          size="small"
                          color="success"
                          title="Invoice unbilled items"
                          onClick={() => {
                            setUnbilledCase(caseItem);
                            setUnbilledOpen(true);
                          }}
                        >
                          <MoneyIcon />
                        </IconButton>
                        <IconButton 
                          size="small" 
                          color="primary"
                          title="Edit"
                          onClick={() => handleEditCase(caseItem)}
                        >
                          <EditIcon />
                        </IconButton>
                        <IconButton size="small" color="error">
                          <DeleteIcon />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            <ListPagination
              page={page}
              limit={limit}
              total={casesTotal}
              onPageChange={setPage}
              onLimitChange={setLimit}
            />
            </>
          )}
        </CardContent>
      </Card>

      {/* Case Dialog */}
      <CaseDialog
        open={dialogOpen}
        onClose={handleDialogClose}
        mode={dialogMode}
        caseData={selectedCase}
      />

      <ReceiptExpenseUpload
        open={expenseOpen}
        onClose={() => setExpenseOpen(false)}
        onSuccess={() => {
          queryClient.invalidateQueries({ queryKey: ['billable-cases'] });
          queryClient.invalidateQueries({ queryKey: ['case-billing-items'] });
        }}
        defaultCaseId={selectedCase?.id}
        defaultClientId={selectedCase?.client?.id}
      />

      <CreateFromUnbilledDialog
        open={unbilledOpen}
        onClose={() => {
          setUnbilledOpen(false);
          setUnbilledCase(null);
        }}
        defaultCaseId={unbilledCase?.id}
        defaultClientId={unbilledCase?.client?.id}
      />
    </Box>
  );
};

export default BillableCasesPage; 